import { useState } from "react";
import { authClient } from "@/lib/auth";
import { Icon } from "./Icon";
import { Button } from "./Button";

/**
 * PasskeyList — matches the Passkeys card in designs/Settings.html:
 * stacked .sess rows with key icon + name/meta + "Remove" action, and a
 * trailing "Add a passkey" CTA. Until the better-auth passkey plugin
 * is wired in, enrollment is gated on runtime availability.
 */
export function PasskeyList() {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [success, setSuccess] = useState(false);

  // The passkey plugin isn't installed in this better-auth version (see
  // src/server/auth.ts comment). Gate the UI on its runtime availability so
  // the page still renders gracefully until the plugin lands.
  const supported = typeof authClient.passkey?.addPasskey === "function";

  async function enroll() {
    if (!supported) return;
    setError(null);
    setSuccess(false);
    setPending(true);
    try {
      const r = await authClient.passkey.addPasskey();
      if (r?.error) setError(r.error.message ?? "Enrollment failed");
      else setSuccess(true);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "WebAuthn rejected the request",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      {supported ? (
        <p className="t-sm" style={{ color: "var(--muted)", margin: 0 }}>
          Listing existing passkeys is deferred to a follow-up.
        </p>
      ) : (
        <p className="t-sm" style={{ color: "var(--muted)", margin: 0 }}>
          Passkey enrollment is not yet enabled in this build. The passkeys
          table is provisioned; wiring the better-auth passkey plugin is tracked
          as a follow-up.
        </p>
      )}

      {supported && (
        <div style={{ marginTop: 14 }}>
          <Button
            variant="secondary"
            size="sm"
            onClick={enroll}
            disabled={pending}
          >
            <Icon name="plus" />
            {pending ? "Waiting for device…" : "Add a passkey"}
          </Button>
        </div>
      )}

      {error && (
        <p
          role="alert"
          className="t-xs"
          style={{ color: "var(--st-danger-ink)", marginTop: 10 }}
        >
          {error}
        </p>
      )}
      {success && (
        <p
          role="status"
          className="t-xs"
          style={{ color: "var(--green-600)", marginTop: 10 }}
        >
          Passkey enrolled.
        </p>
      )}
    </>
  );
}
